import { RamUsersSource, UnassignedMetroSource } from '../../enums/ManagerListSource';
import { getShouldSendUnassignedAsSalesMetro, getShouldSourceRamsFromSalesMetros } from '../../posthog';
import PosthogService from '../../services/posthog/PosthogService';
import { MANAGER_LIST_SOURCES_RESOLVED } from '../actionTypes';

/**
 * Answers which source fills each manager list, deciding once per session.
 *
 * The first caller reads the migration flags and records the answer in
 * `state.users.managerListSources`. Every later caller, and the office profile
 * deciding whether its RAM field is read-only, reads that record instead of the
 * flags. So a PostHog flag reload mid-session (identify triggers one) cannot
 * leave two fetchers, or a fetcher and the profile, acting on different answers.
 *
 * `unassignedMetro` follows the monolith's switch only under sales metros: the
 * monolith list has no reserved row to send.
 *
 * @returns {{
 *   ramUsers: RamUsersSource,
 *   unassignedMetro: UnassignedMetroSource,
 * }}
 */
export const ensureManagerListSources = () => (dispatch, getState) => {
  const recorded = getState().users?.managerListSources;
  if (recorded) {
    return recorded;
  }

  const ramUsers = getShouldSourceRamsFromSalesMetros()
    ? RamUsersSource.SalesMetros
    : RamUsersSource.Monolith;
  const sources = {
    ramUsers,
    unassignedMetro:
      ramUsers === RamUsersSource.SalesMetros && getShouldSendUnassignedAsSalesMetro()
        ? UnassignedMetroSource.SalesMetro
        : UnassignedMetroSource.Sentinel,
  };
  dispatch({ type: MANAGER_LIST_SOURCES_RESOLVED, payload: sources });

  return sources;
};

/**
 * `ensureManagerListSources`, once PostHog has answered its first flag request.
 *
 * The record is kept for the session, so recording it before the flags load
 * would pin their defaults until the next reload: an admin's first visit, with
 * no flags cached, would get the monolith RAM list and the `'unassigned'`
 * sentinel whatever the flags say. Every fetcher waits here instead, and the office
 * profile shows no RAM field until the record exists.
 */
export const resolveManagerListSources = () => async (dispatch, getState) => {
  if (!getState().users?.managerListSources) {
    await PosthogService.getInstance().waitForFlags();
  }

  return dispatch(ensureManagerListSources());
};
